import { scaleBand, scaleLinear } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import type {
  Accessor,
  ChoicesStaticOptions,
  Visualization,
} from '../../types';
import { getElement } from '../../utils/non-empty-array';
import {
  LABELS_HEIGHT,
  LOW_OPACITY,
  MAX_BAR_WIDTH,
  MIN_BAR_HEIGHT,
  MIN_MARGIN_TOP,
  VALUE_TEXT_HEIGHT,
  VALUE_TEXT_MARGIN,
} from '../constants';
import { calculateBarSpacing } from '../utils/calculateBarSpacing';
import { Bar } from '../Bar';
import { BarsAxisLabels } from '../BarsAxisLabels';
import { Score, type CorrectnessIndicatorStatus } from '../Score';
import { SvgWithFont } from '../../utils/SvgWithFont';

export interface BarResult {
  id: number;
  score: number;
  correct_answer?: boolean;
  count?: number;
}

interface QuizBarsProps extends Visualization<BarResult, ChoicesStaticOptions> {
  data: BarResult[];
  shouldShowCorrect: boolean;
  correctAnswerAccessor?: Accessor<ChoicesStaticOptions, boolean>;
  spacingPercentage?: number;
}

export const QuizBars = ({
  colors,
  textColor,
  data,
  staticOptions,
  keyAccessor = (d: BarResult | ChoicesStaticOptions) => String(d.id),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  valueAccessor = (d: BarResult) => d.score,
  correctAnswerAccessor = (d) => d.isMarkedCorrect,
  width: rootWidth,
  height: rootHeight,
  disableAnimations = false,
  shouldShowCorrect = false,
  fontFamily,
  spacingPercentage,
}: QuizBarsProps) => {
  const calculatedSpacing =
    spacingPercentage ??
    calculateBarSpacing({
      containerWidth: rootWidth,
      numBars: staticOptions.length,
    });

  const maxBoundedWidth =
    (MAX_BAR_WIDTH * staticOptions.length) / (1 - calculatedSpacing);
  const boundedWidth = Math.min(rootWidth, maxBoundedWidth);
  const boundsLeftMargin = (rootWidth - boundedWidth) / 2;

  const discreteValues = staticOptions.map(keyAccessor);
  const discreteScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedWidth])
    .paddingInner(calculatedSpacing)
    .paddingOuter(0.00001); // having this at 0 makes the first bar disappear, I have no idea what I'm doing wrong

  const svgHeight = Math.max(rootHeight - LABELS_HEIGHT, 0);
  const boundedHeight = Math.max(svgHeight - MIN_MARGIN_TOP, 0);

  const maxValue = Math.max(...data.map(valueAccessor));
  const domainMax = maxValue > 0 ? maxValue : 1;
  const linearScale = scaleLinear()
    .domain([0, domainMax])
    .range([MIN_BAR_HEIGHT, boundedHeight]);

  if (svgHeight <= 0 || rootWidth <= 0) {
    return null;
  }

  const getCorrectnessIndicatorStatus = (
    choicesData: BarResult,
  ): CorrectnessIndicatorStatus => {
    const staticOption = staticOptions.find(
      (option) => keyAccessor(option) === keyAccessor(choicesData),
    );
    const isCorrect = Boolean(
      staticOption && correctAnswerAccessor(staticOption),
    );
    return {
      isVisible: shouldShowCorrect,
      isCorrect,
    };
  };

  const getRectanglePosition = (d: BarResult) => {
    const value = valueAccessor(d);
    const x = discreteScale(keyAccessor(d));
    const y = boundedHeight - linearScale(value);
    const width = discreteScale.bandwidth();
    return { x, y, width };
  };

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      position="relative"
      aria-hidden
      data-testid="quiz-bars"
    >
      <SvgWithFont width={rootWidth} height={svgHeight} fontFamily={fontFamily}>
        <g
          data-testid="bounds"
          width={boundedWidth}
          height={boundedHeight}
          transform={`translate(${boundsLeftMargin}, ${MIN_MARGIN_TOP})`}
        >
          <g data-name="bars">
            {data.map((d, i) => {
              const value = valueAccessor(d);
              const color = getElement(colors, i);
              const { x, y, width } = getRectanglePosition(d);

              const correctnessIndicatorStatus =
                getCorrectnessIndicatorStatus(d);
              const isCorrectAnswer =
                correctnessIndicatorStatus.isVisible &&
                correctnessIndicatorStatus.isCorrect;

              const isFullOpacity = !shouldShowCorrect || isCorrectAnswer;
              const barOpacity = isFullOpacity ? 1 : LOW_OPACITY;

              if (!x) {
                return null;
              }

              return (
                <g data-value={value} key={d.id}>
                  <Bar
                    color={color}
                    x={x}
                    y={y}
                    width={width}
                    height={linearScale(value)}
                    disableAnimations={disableAnimations}
                    opacity={barOpacity}
                  />
                </g>
              );
            })}
          </g>
        </g>
      </SvgWithFont>
      <Box
        data-testid="scores"
        width={boundedWidth}
        height={boundedHeight}
        top={MIN_MARGIN_TOP}
        left={boundsLeftMargin}
        position="absolute"
        // to enable SVG behind to be clickable
        style={{ pointerEvents: 'none' }}
      >
        {data.map((d) => {
          const value = valueAccessor(d);
          const { x, y: rectangleY, width } = getRectanglePosition(d);
          const correctnessIndicatorStatus = getCorrectnessIndicatorStatus(d);

          if (!x) {
            return null;
          }

          return (
            <Score
              key={x}
              x={x}
              y={rectangleY - VALUE_TEXT_MARGIN}
              barWidth={width}
              textColor={textColor}
              height={VALUE_TEXT_HEIGHT}
              scoreDisplayText={value.toString()}
              correctnessIndicatorStatus={correctnessIndicatorStatus}
              disableAnimations={disableAnimations}
            />
          );
        })}
      </Box>
      <BarsAxisLabels
        staticOptions={staticOptions}
        labelAccessor={labelAccessor}
        keyAccessor={keyAccessor}
        discreteScale={discreteScale}
        totalWidth={boundedWidth}
        totalHeight={LABELS_HEIGHT}
        marginLeft={boundsLeftMargin}
        marginTop={20}
        color={textColor}
      />
    </Box>
  );
};
